import { z } from 'zod'
import { LocaleCode } from './enums.js'

/**
 * D7 field attributes — Part Three D7, register §6/§8.
 *
 * These extend the existing `Field` shape (`manifest.ts`) rather than defining a
 * parallel catalogue: stable ID, type, owner, page, geometry, label, tooltip,
 * required state, editable/locked status, default, formatting and validation. Every
 * addition is optional, so manifests written before v1.7 still validate unchanged.
 *
 * The text and tooltip maxima are register values (`text_field_max_scalars`,
 * `tooltip_max_scalars`) and are applied by `validateFieldLimits` — no C/D limit is a
 * literal here.
 */

const RoleId = z.string().min(1).max(64)

/**
 * Field owner — D4/D7. Ownership is a stable participant reference: when a participant
 * is removed the sender reassigns or removes the field, and activation is blocked while
 * an owner is invalid. `UNASSIGNED` is a legitimate transient state in the editor.
 */
export const FieldOwner = z
  .object({
    kind: z.enum(['SENDER', 'SIGNER_ROLE', 'UNASSIGNED']),
    role_id: RoleId.optional(),
  })
  .strict()
  .superRefine((owner, ctx) => {
    if (owner.kind === 'SIGNER_ROLE' && owner.role_id === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a signer-owned field names its role',
        path: ['role_id'],
      })
    }
    if (owner.kind !== 'SIGNER_ROLE' && owner.role_id !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'role_id is only valid for a signer-owned field',
        path: ['role_id'],
      })
    }
  })
export type FieldOwner = z.infer<typeof FieldOwner>

/** Display formatting for a resolved value. `NONE` keeps the raw value. */
export const FieldFormatting = z
  .object({
    kind: z.enum(['NONE', 'PLAIN_TEXT', 'NUMBER', 'CURRENCY', 'PERCENT', 'DATE']),
    decimals: z.number().int().nonnegative().optional(),
    locale_code: LocaleCode.optional(),
  })
  .strict()
  .superRefine((formatting, ctx) => {
    const numeric =
      formatting.kind === 'NUMBER' ||
      formatting.kind === 'CURRENCY' ||
      formatting.kind === 'PERCENT'
    if (formatting.decimals !== undefined && !numeric) {
      ctx.addIssue({
        code: 'custom',
        message: 'decimals is only valid for numeric formatting',
        path: ['decimals'],
      })
    }
  })
export type FieldFormatting = z.infer<typeof FieldFormatting>

/**
 * Sender-provided default value — D7, D9. `SENDER_PREFILLED_IMMUTABLE` values are the
 * only sender-side values a formula may depend on besides the signer's own fields.
 */
export const FieldDefault = z
  .object({
    source: z.enum(['SENDER_PREFILLED_IMMUTABLE', 'SENDER_PREFILLED_EDITABLE']),
    value: z.string(),
  })
  .strict()
export type FieldDefault = z.infer<typeof FieldDefault>
